import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Container from "../Container/Container";
import s from "./Header.module.scss";

import { useClinicInfo } from "../../../api/queries/useClinicInfo";
import { toMap } from "../../../app/clinicInfoMap";

import logoMarkUrl  from "../../../assets/brand/logo-mark.png";
import Modal from "../Modal/Modal";
import Feedback from "components/sections/Feedback/Feedback";

const BRAND_COLOR = "#575757";

function toTel(phone: string) {
  return phone.replace(/[^\d+]/g, "");
}

const LogoMark = () => (<img src={logoMarkUrl} className={s.logoSvg} alt="logo"></img>)

function getPrimaryPhone(cfg: Record<string, string | undefined>) {
  const raw = cfg["contacts.phones"];
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const first = parsed.find((x) => typeof x === "string" && x.trim());
        if (first) return String(first).trim();
      }
    } catch {}
  }
  return (cfg["contacts.phone"] || "").trim();
}

function toMobileBrandTitle(title: string) {
  const t = title.trim();
  const m = t.match(/^Ю\s*([-–—])\s*(.+)$/i);
  if (m) return `${m[1]}${m[2]}`;
  if (/^Ю/i.test(t)) return t.replace(/^Ю\s*/i, "-");
  return t;
}

function getPageTitle(pathname: string, brandTitle: string) {
  const brand = brandTitle.trim() || "Ю-ДЕНТ";
  if (pathname === "/") return `${brand} — стоматология`;
  if (pathname === "/services") return `Услуги | ${brand}`;
  if (pathname.startsWith("/services/")) return `Услуга | ${brand}`;
  if (pathname === "/price") return `Цены | ${brand}`;
  if (pathname === "/blog") return `Блог | ${brand}`;
  if (pathname.startsWith("/blog/")) return `Статья | ${brand}`;
  if (pathname === "/contacts") return `Контакты | ${brand}`;
  if (pathname === "/team") return `Команда | ${brand}`;
  if (pathname.startsWith("/team/")) return `Специалист | ${brand}`;
  if (pathname === "/document") return `Правовая информация | ${brand}`;
  if (pathname === "/privacy-policy") return `Политика конфиденциальности | ${brand}`;
  return brand;
}

function setFaviconLinks(href: string) {
  const rels = ["icon", "shortcut icon", "apple-touch-icon"];
  rels.forEach((rel) => {
    let link = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", rel);
      document.head.appendChild(link);
    }
    link.setAttribute("href", href);
    if (rel === "icon" || rel === "shortcut icon") {
      link.setAttribute("type", "image/svg+xml");
    }
  });
}

async function ensureBrandFavicon(svgUrl: string, color: string) {
  try {
    const response = await fetch(svgUrl);
    const sourceSvg = await response.text();
    const withBrandColor = sourceSvg
      .replace(/currentColor/g, color)
      .replace(/<svg\b([^>]*)>/i, `<svg$1 color="${color}">`);
    const encoded = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(withBrandColor)}`;
    setFaviconLinks(encoded);
  } catch {
    setFaviconLinks(svgUrl);
  }
}

function normalizeExternalLink(href: string) {
  const trimmed = href.trim();
  if (!trimmed) return "";
  if (/^(https?:)?\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed.replace(/^\/+/, "")}`;
}

const getIconMaskStyle = (iconSrc: string): React.CSSProperties => ({
  WebkitMaskImage: `url(${iconSrc})`,
  maskImage: `url(${iconSrc})`,
});

type NavItem =
  | { label: string; to: string; end?: boolean }
  | { label: string; href: string; external?: boolean };

export default function Header() {
  const { data, isLoading, isError } = useClinicInfo();
  const cfg = toMap(data ?? []) as Record<string, string | undefined>;

  const phone = getPrimaryPhone(cfg) || "+7 (000) 000-00-00";
  const tel = toTel(phone);

  const brandTitle = (cfg["header.brand.title"] || "ПРОФЕССИОНАЛЬНАЯ").trim();
  const brandSubtitle = (cfg["header.brand.subtitle"] || "СТОМАТОЛОГИЯ").trim();
  const mobileTitle = toMobileBrandTitle(brandTitle);

  const socialItems = [
    {
      key: "vk",
      href: (cfg["contacts.social.vk"] || "").trim(),
      label: "VK",
      iconSrc: "/icons/vk.svg",
    },
    {
      key: "max",
      href: (cfg["contacts.social.max"] || "").trim(),
      label: "MAX",
      iconSrc: "/icons/max.svg",
    },
  ].filter((i) => Boolean(i.href));

  const navItems: NavItem[] = useMemo(
    () => [
      { label: "Главная", to: "/", end: true },
      { label: "Услуги", to: "/services" },
      { label: "Цены", to: "/price" },
      { label: "Команда", to: "/team" },
      { label: "Контакты", to: "/contacts" },
      { label: "Акции", to: "/blog" },
    ],
    []
  );

  const [open, setOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const phoneButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    ensureBrandFavicon(logoMarkUrl, BRAND_COLOR);
  }, []);

  useEffect(() => {
    document.title = getPageTitle(location.pathname, brandTitle);
  }, [location.pathname, brandTitle]);

  const isAnchorActive = (href: string) => location.pathname === "/" && location.hash === href;

  const scrollTopInstant = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  };

  const onRouteClick = (to: string, e?: React.MouseEvent) => {
    setOpen(false);
    if (location.pathname === to) {
      if (e) e.preventDefault();
      scrollTopInstant();
      return;
    }
    scrollTopInstant();
  };

  const goHomeByLogo = (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen(false);
    navigate("/", { replace: location.pathname === "/" });
    scrollTopInstant();
  };

  const handleOpenFeedbackModal = () => setOpenModal(true);
  const handleCloseFeedbackModal = () => {
    setOpenModal(false);
    phoneButtonRef.current?.blur();
  };

  const renderLink = (item: NavItem, kind: "desktop" | "mobile") => {
    if ("to" in item) {
      return (
        <NavLink
          key={item.label}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            kind === "desktop"
              ? isActive
                ? s.linkActive
                : s.link
              : isActive
              ? `${s.mobileLink} ${s.mobileLinkActive}`
              : s.mobileLink
          }
          onClick={(e) => onRouteClick(item.to, e)}
        >
          {item.label}
        </NavLink>
      );
    }

    if (item.external) {
      const cls = kind === "desktop" ? s.link : s.mobileLink;
      return (
        <a
          key={item.label}
          className={cls}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setOpen(false)}
        >
          {item.label}
        </a>
      );
    }

    const active = isAnchorActive(item.href);
    const cls =
      kind === "desktop"
        ? active
          ? s.linkActive
          : s.link
        : active
        ? `${s.mobileLink} ${s.mobileLinkActive}`
        : s.mobileLink;

    return (
      <a key={item.label} className={cls} href={item.href} onClick={() => setOpen(false)}>
        {item.label}
      </a>
    );
  };

  return (
    <header className={s.header}>
      <Container className={s.headerContainer}>
        <div className={s.row}>
          <NavLink to="/" className={s.brand} onClick={goHomeByLogo} aria-label="На главную">
            <span className={s.logoWrap} aria-hidden="true">
              <LogoMark />
            </span>

            <span className={s.wordmark}>
              <span className={s.wordmarkTitle}>
                <span className={s.titleDesktop}>{brandTitle}</span>
              </span>

              <span className={s.wordmarkTag}>{brandSubtitle}</span>
            </span>
          </NavLink>

          <nav className={s.nav}>{navItems.map((i) => renderLink(i, "desktop"))}</nav>

          <div className={s.right}>
            {socialItems.length > 0 && (
              <div className={s.socials}>
                {socialItems.map((item) => (
                  <a
                    key={item.key}
                    href={normalizeExternalLink(item.href)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={item.label}
                    title={item.label}
                    className={s.socialLink}
                  >
                    <span
                      className={`${s.socialIcon} ${s[`socialIcon_${item.key}`]}`}
                      style={getIconMaskStyle(item.iconSrc)}
                      aria-hidden="true"
                    />
                  </a>
                ))}
              </div>
            )}
            {isLoading ? (
              <span className={s.phonePlaceholder}>...</span>
            ) : isError ? (
              <span className={s.phonePlaceholder}>нет связи</span>
            ) : (
              <button
                ref={phoneButtonRef}
                type="button"
                className={s.phone}
                onClick={handleOpenFeedbackModal}
                aria-haspopup="dialog"
                aria-expanded={openModal}
              >
                {phone}
              </button>
            )}

            <button
              type="button"
              className={s.burger}
              aria-label={open ? "Закрыть меню" : "Открыть меню"}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
            >
              <span className={s.burgerIcon} />
            </button>
          </div>

          <Modal isOpen={openModal} onClose={handleCloseFeedbackModal} flush>
            <Feedback />
          </Modal>
        </div>
      </Container>

      <div className={`${s.mobile} ${open ? s.mobileOpen : ""}`} aria-hidden={!open}>
        <div className={s.mobileBackdrop} onClick={() => setOpen(false)} />
        <div className={s.mobilePanel}>
          <div className={s.mobileNav}>{navItems.map((i) => renderLink(i, "mobile"))}</div>

          {!isLoading && !isError && (
            <a className={s.mobilePhone} href={`tel:${tel}`} onClick={() => setOpen(false)}>
              {phone}
            </a>
          )}
        </div>
      </div>
    </header>
  );
}
